import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useRealtime, usePolling, fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChefHat, Clock, MapPin, ShoppingBag, Truck, Volume2, VolumeX, LogOut, Printer, Ban, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ORDER_STATUS_LABELS, getContextualStatusLabel, getNextActionLabel } from '@/lib/orderStatus';
import { useBranch } from '@/lib/branch';
import { unlockAudio, playKitchenChime } from '@/lib/chime';
import { KioskLockBar } from '@/lib/kioskLock';
import { StaffRail, OrderTypeBadge } from '@/components/layout/StaffRail';
import { printKitchenTicket } from '@/components/pos/OrdersQueue';
import DispatchPreviewDialog from '@/components/DispatchPreviewDialog';

const LANES = [
  { key: 'received', label: 'NEW ORDERS', color: 'border-l-blue-500' },
  { key: 'preparing', label: 'COOKING', color: 'border-l-[color:hsl(var(--primary))]' },
  { key: 'ready', label: 'READY (pickup · serve · dispatch)', color: 'border-l-emerald-600' },
  { key: 'out_for_delivery', label: 'EN ROUTE TO CUSTOMER', color: 'border-l-violet-500' },
];

export default function KDS() {
  const { user, logout } = useAuth();
  const branchCtx = useBranch();
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [dispatchOrder, setDispatchOrder] = useState(null); // open dispatch preview for this order
  // Track which Ready orders have had their kitchen ticket printed. The
  // action button (Dispatch / Hand to customer / Mark served) stays disabled
  // until the ticket is printed so packaging always carries the slip.
  const [printedTickets, setPrintedTickets] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('kds:printedTickets') || '[]')); }
    catch (e) { return new Set(); }
  });
  const markPrinted = (orderId) => {
    setPrintedTickets((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      try { sessionStorage.setItem('kds:printedTickets', JSON.stringify([...next])); } catch (e) { /* ignore */ }
      return next;
    });
  };
  const [soundOn, setSoundOn] = useState(true);
  // Kitchen day reset hour, loaded from /api/policies/public so the
  // header can show the operator the current "kitchen day" boundary.
  // Falls back to 0 (midnight) if the policy fetch fails.
  const [resetHour, setResetHour] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get('/policies/public').then((r) => {
      if (alive) setResetHour(Math.max(0, Math.min(23, parseInt(r.data?.kitchen_day_reset_hour ?? 0, 10) || 0)));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Compute which calendar day the lanes currently belong to. If we
  // haven't crossed today's reset hour yet, the "kitchen day" is still
  // yesterday's date. This matches the backend boundary logic.
  const { kitchenDayLabel, resetHourLabel } = useMemo(() => {
    const now = new Date();
    const todayBoundary = new Date(now);
    todayBoundary.setHours(resetHour, 0, 0, 0);
    const day = now < todayBoundary
      ? new Date(now.getTime() - 24 * 60 * 60 * 1000)
      : now;
    const dayStr = day.toISOString().slice(0, 10);
    const hh = String(resetHour).padStart(2, '0');
    return { kitchenDayLabel: dayStr, resetHourLabel: `${hh}:00` };
  }, [resetHour]);
  // Cancelled order acknowledgement — surfaces orders that were cancelled
  // *while the kitchen was viewing them* so the kitchen team sees the
  // cancellation event instead of the order silently disappearing from the
  // lane (which they may not notice).
  //
  // We persist BOTH a top-of-page banner chip AND keep the order card itself
  // visible inside its previous lane (pinned to the top) so the kitchen can
  // see in-context which dish stopped. The card is non-actionable; the
  // operator must explicitly dismiss it.
  //
  // Each entry tracks the order's *previous* status (the lane it lived in
  // before the cancel event) so we can render the card back into that lane.
  const [recentlyCancelled, setRecentlyCancelled] = useState([]); // [{id, order_number, order_type, table_number, at, prev_status, order}]
  const dismissCancelled = (id) => setRecentlyCancelled((prev) => prev.filter((c) => c.id !== id));
  // Auto-expire cancelled-acknowledgements after 15 minutes (gives the
  // kitchen plenty of time to physically discard the dish or roll it back).
  useEffect(() => {
    if (!recentlyCancelled.length) return undefined;
    const interval = setInterval(() => {
      const now = Date.now();
      setRecentlyCancelled((prev) => prev.filter((c) => now - c.at < 15 * 60 * 1000));
    }, 30000);
    return () => clearInterval(interval);
  }, [recentlyCancelled.length]);
  const nav = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add('kds-dark');
    return () => document.documentElement.classList.remove('kds-dark');
  }, []);

  // Browsers block audio until the user clicks the page at least once.
  // Attach a one-time gesture listener so the chime is unlocked silently
  // the moment the operator interacts with anything.
  useEffect(() => {
    const handler = () => { unlockAudio(); };
    window.addEventListener('pointerdown', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  // Sync KDS branch with the global active branch from BranchProvider
  useEffect(() => {
    setBranches(branchCtx.branches || []);
    // "All branches" doesn't fit KDS (single-kitchen view) — fall back to first available
    if (branchCtx.isAllBranches) {
      const first = (branchCtx.branches || [])[0]?.id || '';
      setBranchId(first);
    } else if (branchCtx.activeId) {
      setBranchId(branchCtx.activeId);
    }
  }, [branchCtx.activeId, branchCtx.isAllBranches, branchCtx.branches]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/orders/active', { params: { branch_id: branchId || undefined } });
      setOrders(data || []);
    } catch (e) {
      toast.error('Failed to load orders');
    }
  }, [branchId]);

  useEffect(() => { if (branchId) load(); }, [load, branchId]);

  useRealtime(
    branchId ? [`kds:${branchId}`] : [],
    useCallback((msg) => {
      if (!msg?.order) return;
      if (msg.type === 'order_created') {
        if (soundOn) {
          playKitchenChime();
        }
        toast(`\ud83d\udd14 New order #${msg.order.order_number}`);
      } else if (msg.type === 'order_status_changed' && msg.order.status === 'cancelled') {
        // Surface cancellation as an explicit acknowledgement so the kitchen
        // team is aware the order has been killed (not just silently gone).
        // The order card stays pinned to the top of the lane it lived in
        // before cancellation — non-actionable — until the operator
        // dismisses it.
        const o = msg.order;
        // Figure out which KDS lane the order was sitting in before this
        // event. Prefer the locally-known status; fall back to the
        // second-to-last entry in status_history; finally default to
        // 'received' so it shows up in NEW ORDERS.
        const prevKnown = orders.find((x) => x.id === o.id);
        const history = Array.isArray(o.status_history) ? o.status_history : [];
        const prevFromHistory = history.length >= 2 ? history[history.length - 2]?.status : null;
        const prev_status_raw = prevKnown?.status || prevFromHistory || 'received';
        const KDS_LANES = ['received', 'preparing', 'ready', 'out_for_delivery'];
        const prev_status = KDS_LANES.includes(prev_status_raw) ? prev_status_raw : 'received';
        setRecentlyCancelled((prev) => {
          if (prev.some((c) => c.id === o.id)) return prev;
          return [
            {
              id: o.id,
              order_number: o.order_number,
              order_type: o.order_type,
              table_number: o.table_number,
              at: Date.now(),
              prev_status,
              order: { ...o, status: 'cancelled' },
            },
            ...prev,
          ].slice(0, 8);
        });
        toast.error(`\u274c Order #${o.order_number} cancelled \u2014 stop preparing`, { duration: 10000 });
        if (soundOn) {
          try { playKitchenChime(); } catch (e) { /* noop */ }
        }
      }
      load();
    }, [load, soundOn, orders]),
  );

  // Safety-net polling in case WebSocket cannot connect
  usePolling(load, 8000, [branchId]);

  const grouped = useMemo(() => {
    const g = { received: [], preparing: [], ready: [], out_for_delivery: [] };
    // 1. Place every currently-active order in its status lane.
    for (const o of orders) {
      if (g[o.status]) g[o.status].push(o);
    }
    // 2. Inject the cancelled-but-acknowledged cards into the lane they
    //    were sitting in before cancellation. They pin to the TOP of the
    //    lane so the kitchen sees them first and physically discards the
    //    dish. The card itself renders as non-actionable (see below).
    for (const c of recentlyCancelled) {
      if (!g[c.prev_status]) continue;
      // Skip if for some reason the order is still in the active list as
      // non-cancelled (race with /orders/active before cancel propagates).
      const alreadyActive = g[c.prev_status].some((o) => o.id === c.id);
      if (alreadyActive) {
        g[c.prev_status] = g[c.prev_status].filter((o) => o.id !== c.id);
      }
      const cancelledOrder = {
        ...c.order,
        _kdsCancelled: true,
        _cancelledAt: c.at,
      };
      g[c.prev_status].unshift(cancelledOrder);
    }
    return g;
  }, [orders, recentlyCancelled]);

  const advance = async (order) => {
    // Special case: ready → delivery means dispatching. We open a preview
    // dialog instead of silently transitioning — the cashier needs to see
    // who will be SMS'd before broadcasting.
    if (order.status === 'ready' && order.order_type === 'delivery') {
      setDispatchOrder(order);
      return;
    }
    const next = {
      received: 'preparing',
      preparing: 'ready',
      ready: order.order_type === 'delivery' ? 'out_for_delivery' : 'completed',
      out_for_delivery: 'delivered',
    }[order.status];
    if (!next) return;
    try {
      await api.patch(`/orders/${order.id}/status`, { status: next });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    }
  };

  // Print the kitchen ticket attached to the food. Augment the order doc with
  // the branch name so the printed slip shows context (e.g. "City Grill Downtown").
  const printTicket = (order) => {
    const branch = branches.find((b) => b.id === order.branch_id);
    printKitchenTicket({ ...order, branch_name: branch?.name });
    markPrinted(order.id);
  };

  const onDispatched = async (_result) => {
    // After dispatch the order should immediately move to out_for_delivery so
    // the kitchen lane reflects reality. The backend already flips driver_id
    // on acceptance; we eagerly transition the order status here so the KDS
    // view doesn't get stuck in "Ready" until the driver hits Accept.
    if (!dispatchOrder) return;
    try {
      await api.patch(`/orders/${dispatchOrder.id}/status`, { status: 'out_for_delivery' });
    } catch (e) { /* non-fatal */ }
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <KioskLockBar title="KDS" position="bottom-right" />
      <StaffRail variant="dark" />
      <header className="sticky top-[42px] z-20 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"><ChefHat className="h-4 w-4" /></span>
            <div>
              <h1 className="font-display text-lg font-semibold">Kitchen Display</h1>
              <p className="text-xs text-muted-foreground">Live order queue &middot; {user?.full_name}</p>
            </div>
          </div>
          {/* Operational-day indicator — shows the operator which "kitchen day"
              the lanes currently belong to and when they auto-reset (any
              unfinished orders flip to 'overdue' at that hour). */}
          <p data-testid="kds-day-indicator" className="ml-12 text-[11px] text-muted-foreground">
            Kitchen day: <span className="font-mono font-semibold text-foreground">{kitchenDayLabel}</span>
            &nbsp;&middot;&nbsp;reset @ <span className="font-mono font-semibold text-foreground">{resetHourLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Branch lock — kitchen staff are pinned to the branch they were
              enrolled at. Only owners can switch branches anywhere in the
              app. We show the active branch as a read-only chip so the
              cook still knows which store the lanes belong to. */}
          <span data-testid="kds-branch-lock" className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-muted/40 px-3 text-xs text-muted-foreground">
            {branches.find((b) => b.id === branchId)?.name || '\u2014'}
          </span>
          <Button
            data-testid="kds-sound-toggle-button"
            size="sm"
            variant={soundOn ? 'default' : 'secondary'}
            onClick={async () => {
              const next = !soundOn;
              setSoundOn(next);
              if (next) {
                // The click is a user gesture — unlock audio context and play a test chime
                await unlockAudio();
                playKitchenChime();
                toast.success('Sound enabled — you’ll hear a chime on every new order');
              } else {
                toast('Sound muted');
              }
            }}
          >
            {soundOn ? <Volume2 className="mr-1 h-4 w-4" /> : <VolumeX className="mr-1 h-4 w-4" />} Sound
          </Button>
          <Button data-testid="kds-logout-button" size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      {/* Cancelled-order acknowledgement banner — appears when an order is
          cancelled (from POS or by the customer) while the kitchen has it
          visible. The kitchen team must dismiss each entry to confirm they
          stopped preparing it. Entries also auto-expire after 5 minutes. */}
      {recentlyCancelled.length > 0 && (
        <div className="mx-4 mt-4 rounded-lg border-2 border-rose-500 bg-rose-50 p-3" data-testid="kds-cancelled-banner">
          <div className="mb-2 flex items-center gap-2 text-rose-900">
            <Ban className="h-4 w-4" />
            <p className="text-sm font-semibold uppercase tracking-wider">
              Cancellation alert &mdash; stop preparing
            </p>
          </div>
          <ul className="flex flex-wrap gap-2">
            {recentlyCancelled.map((c) => {
              const tag = c.order_type === 'dine_in' ? `Table ${c.table_number ?? '\u2014'}`
                : c.order_type === 'pickup' ? 'Pickup'
                : c.order_type === 'delivery' ? 'Delivery' : c.order_type;
              return (
                <li key={c.id} data-testid="kds-cancelled-chip" className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-white px-2.5 py-1 text-sm text-rose-900 shadow-sm">
                  <span className="font-display font-bold">#{c.order_number}</span>
                  <span className="text-xs text-rose-700">&middot; {tag}</span>
                  <button
                    type="button"
                    data-testid="kds-cancelled-dismiss"
                    onClick={() => dismissCancelled(c.id)}
                    className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-rose-700 hover:bg-rose-100"
                    aria-label="Dismiss cancellation notice"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        {LANES.map((lane) => (
          <div key={lane.key} data-testid={`kds-lane-${lane.key}`} className="flex min-h-[70vh] flex-col rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <h2 className="text-sm font-semibold tracking-wider">{lane.label}</h2>
              <Badge variant="secondary" className="text-xs">{grouped[lane.key].length}</Badge>
            </div>
            <ScrollArea className="flex-1">
              <div className="flex flex-col gap-3 p-3">
                {grouped[lane.key].map((o) => {
                  const isCancelled = !!o._kdsCancelled;
                  return (
                  <Card
                    key={o.id}
                    data-testid={isCancelled ? 'kds-ticket-card-cancelled' : 'kds-ticket-card'}
                    className={`border-l-8 ${isCancelled ? 'border-l-rose-500 bg-rose-50/50 dark:bg-rose-950/30 ring-2 ring-rose-400/60' : lane.color}`}
                  >
                    <CardContent className="p-3">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <OrderTypeBadge orderType={o.order_type} tableNumber={o.table_number} size="lg" />
                        {isCancelled ? (
                          <span data-testid="kds-cancelled-status" className="inline-flex items-center gap-1 rounded-full border-2 border-rose-500 bg-rose-500/15 px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                            <Ban className="h-3 w-3" /> Cancelled &mdash; stop preparing
                          </span>
                        ) : (
                          <span data-testid="kds-contextual-status" className="inline-flex items-center rounded-full border border-foreground/15 bg-foreground/5 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">
                            {getContextualStatusLabel(o.status, o.order_type, o.table_number)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-display text-xl font-semibold leading-none ${isCancelled ? 'text-rose-800 dark:text-rose-300 line-through decoration-2' : ''}`}>#{o.order_number}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {o.customer_name || 'Guest'}{o.customer_phone ? ` \u00b7 ${o.customer_phone}` : ''}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs"><Clock className="h-3 w-3" /> {fmtTimeAgo(o.created_at)}</span>
                      </div>
                      <ul className={`mt-2 space-y-1 text-sm ${isCancelled ? 'opacity-70' : ''}`}>
                        {o.items?.map((it, idx) => (
                          <li key={idx} className={`flex items-start justify-between gap-2 ${isCancelled ? 'line-through decoration-rose-400' : ''}`}>
                            <span><span className="font-medium">{it.quantity}\u00d7</span> {it.name}{it.special_instructions ? <span className="block pl-4 text-xs italic text-muted-foreground">\u2192 {it.special_instructions}</span> : null}</span>
                            <span className="text-xs text-muted-foreground tabular">{fmtMoney(it.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">{o.customer_name}</span>
                        <div className="flex items-center gap-1.5">
                          {isCancelled ? (
                            <>
                              <Button
                                data-testid="kds-cancelled-advance-button"
                                size="sm"
                                variant="secondary"
                                disabled
                                className="cursor-not-allowed border border-rose-300 bg-rose-100 text-rose-700 opacity-100 hover:bg-rose-100 dark:bg-rose-900/40 dark:text-rose-200"
                              >
                                <Ban className="mr-1 h-4 w-4" /> Cancelled
                              </Button>
                              <Button
                                data-testid="kds-cancelled-dismiss-card"
                                size="sm"
                                variant="ghost"
                                onClick={() => dismissCancelled(o.id)}
                                title="Acknowledge cancellation and remove from lane"
                                className="text-rose-700 hover:bg-rose-100 dark:text-rose-300 dark:hover:bg-rose-900/40"
                              >
                                <X className="mr-1 h-4 w-4" /> Dismiss
                              </Button>
                            </>
                          ) : (
                            <>
                              {o.status === 'ready' && (
                                <Button
                                  data-testid="kds-print-ticket-button"
                                  size="sm"
                                  variant={printedTickets.has(o.id) ? 'ghost' : 'default'}
                                  onClick={() => printTicket(o)}
                                  title="Print the kitchen ticket to attach to the package"
                                >
                                  <Printer className="mr-1 h-4 w-4" /> {printedTickets.has(o.id) ? 'Re-print' : 'Ticket'}
                                </Button>
                              )}
                              {(() => {
                                const isReady = o.status === 'ready';
                                const needsTicket = isReady && !printedTickets.has(o.id);
                                return (
                                  <Button
                                    data-testid="kds-advance-button"
                                    size="sm"
                                    variant={needsTicket ? 'secondary' : 'default'}
                                    onClick={() => advance(o)}
                                    disabled={needsTicket}
                                    title={needsTicket ? 'Print the kitchen ticket first' : undefined}
                                  >
                                    {getNextActionLabel(o.status, o.order_type)}
                                  </Button>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
                {!grouped[lane.key].length && (
                  <div className="flex h-32 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">All caught up.</div>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
      <DispatchPreviewDialog
        open={!!dispatchOrder}
        onOpenChange={(o) => { if (!o) setDispatchOrder(null); }}
        order={dispatchOrder}
        onDispatched={onDispatched}
      />
    </div>
  );
}
